import { useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { LogOut, Sun, Moon, ChevronDown, Loader2, User } from 'lucide-react';
import { useEmployeeController } from '@/controllers/employee/useEmployeeController';
import { themeStore } from '@/stores/theme.store';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface UserProfileProps {
  userLogin: string;
  onLogout: () => void;
}

export const UserProfile = observer(function UserProfile({
  userLogin,
  onLogout,
}: UserProfileProps) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data: employee, isLoading } = useEmployeeController(userLogin || undefined);
  const isDark = themeStore.theme === 'dark';

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const displayName = employee
    ? [employee.lastname, employee.firstname, employee.middlename].filter(Boolean).join(' ')
    : '';
  const initials = displayName
    .split(' ')
    .slice(0, 2)
    .map((word: string) => word[0])
    .join('')
    .toUpperCase();

  return (
    <div className='relative' ref={dropdownRef}>
      <Button
        variant='outline'
        onClick={() => setOpen((prev) => !prev)}
        className='gap-2.5 px-3 py-2'
      >
        <div className='w-7 h-7 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0'>
          {isLoading ? (
            <Loader2 className='w-3.5 h-3.5 text-primary animate-spin' />
          ) : (
            <span className='text-xs font-semibold text-primary'>
              {initials || <User className='w-3.5 h-3.5' />}
            </span>
          )}
        </div>
        <div className='text-left hidden sm:block'>
          <p className='text-sm font-medium text-foreground leading-tight'>{userLogin}</p>
        </div>
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 text-muted-foreground transition-transform shrink-0',
            open && 'rotate-180'
          )}
        />
      </Button>

      {open && (
        <div
          className='absolute right-0 top-full mt-1.5 w-64 rounded-xl border border-border/50 overflow-hidden z-50 shadow-xl'
          style={{ backgroundColor: 'var(--color-card)' }}
        >
          <div className='px-4 py-3.5 border-b border-border/20'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0'>
                <span className='text-sm font-semibold text-primary'>
                  {initials || userLogin[0]?.toUpperCase()}
                </span>
              </div>
              <div className='min-w-0'>
                <p className='text-sm font-semibold text-foreground truncate'>{userLogin}</p>
                {displayName && (
                  <p className='text-xs text-muted-foreground truncate' title={displayName}>
                    {displayName}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className='p-1.5'>
            <Button
              variant='ghost'
              onClick={() => {
                themeStore.toggle();
                setOpen(false);
              }}
              className='w-full justify-start gap-2.5 px-3 py-2 h-auto rounded-lg text-foreground hover:bg-muted/50'
            >
              {isDark ? (
                <Sun className='w-4 h-4 text-muted-foreground' />
              ) : (
                <Moon className='w-4 h-4 text-muted-foreground' />
              )}
              {isDark ? 'Светлая тема' : 'Тёмная тема'}
            </Button>
            <Button
              variant='ghost'
              onClick={() => {
                onLogout();
                setOpen(false);
              }}
              className='w-full justify-start gap-2.5 px-3 py-2 h-auto rounded-lg text-destructive hover:text-destructive hover:bg-destructive/10'
            >
              <LogOut className='w-4 h-4' />
              Выйти
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});
